import * as React from 'react'
import { isFunction } from '@applift/factor'
import { GridPrivateApiCommon } from '../../models/api/gridApiCommon'

export const useGridNativeEventListener = <
  PrivateApi extends GridPrivateApiCommon,
  K extends keyof HTMLElementEventMap,
>(
  apiRef: React.MutableRefObject<PrivateApi>,
  ref:
    | React.MutableRefObject<HTMLDivElement | null>
    | (() => HTMLElement | undefined | null),
  eventName: K,
  handler?: (event: HTMLElementEventMap[K]) => any,
  options?: AddEventListenerOptions
) => {
  const [added, setAdded] = React.useState(false)
  const handlerRef = React.useRef(handler)

  const wrapHandler = React.useCallback((event: HTMLElementEventMap[K]) => {
    return handlerRef.current && handlerRef.current(event)
  }, [])

  React.useEffect(() => {
    handlerRef.current = handler
  }, [handler])

  React.useEffect(() => {
    let targetElement: HTMLElement | null | undefined

    if (isFunction(ref)) {
      targetElement = ref()
    } else {
      targetElement = ref && ref.current ? ref.current : null
    }

    if (targetElement && eventName && !added) {
      targetElement.addEventListener(eventName, wrapHandler, options)
      const boundElem = targetElement
      setAdded(true)

      const unsubscribe = () => {
        boundElem.removeEventListener(eventName, wrapHandler, options)
      }

      apiRef.current.subscribeEvent('unmount', unsubscribe)
    }
  }, [ref, wrapHandler, eventName, added, options, apiRef])
}
